import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Input, Select, Space, message, Switch, Tag, Modal, Form, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, SearchOutlined, ClearOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall } from '../../utils/helper';
import './masters-config.css';

const { Option } = Select;

// Modalities that must never be part of the configurable modality set (T001 - item e).
const EXCLUDED_MODALITY_CODES = ['CR', 'RF', 'AN', 'SC'];

const ModalityMaster = () => {
  const [modalitiesList, setModalitiesList] = useState({ data: [], loading: false, error: null });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [form] = Form.useForm();

  const fetchModalities = useCallback(async () => {
    setModalitiesList(prev => ({ ...prev, loading: true, error: null }));
    try {
      // all=true returns active + inactive so admins can manage everything.
      const response = await makeGetCall('/modalities?all=true');
      if (response?.data?.success) {
        setModalitiesList({ data: response.data.data || [], loading: false, error: null });
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch modalities');
      }
    } catch (error) {
      setModalitiesList({ data: [], loading: false, error: error.message || 'Failed to fetch modalities' });
      message.error('Failed to fetch modalities');
    }
  }, []);

  useEffect(() => {
    fetchModalities();
  }, [fetchModalities]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      modality_label: record.label,
      modality_code: record.value,
      is_active: record.active,
    });
    setModalVisible(true);
  };

  const handleToggleActive = async (record, checked) => {
    setTogglingId(record.id);
    try {
      const res = await makePostCall('/modality', {
        modality_id: record.id,
        is_active: checked,
      });
      if (res?.data?.success) {
        message.success(`Modality ${checked ? 'activated' : 'deactivated'}`);
        fetchModalities();
      } else {
        throw new Error(res?.data?.message || 'Failed to update modality');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to update modality');
    } finally {
      setTogglingId(null);
    }
  };

  const handleSubmit = async (values) => {
    const code = String(values.modality_code || '').trim().toUpperCase();

    // Enforce the T001 exclusion rule only on create (code is immutable on edit).
    if (!editingRecord && EXCLUDED_MODALITY_CODES.includes(code)) {
      message.error(`${code} cannot be configured as a modality.`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = editingRecord
        ? {
            modality_id: editingRecord.id,
            modality_label: values.modality_label?.trim(),
            is_active: values.is_active,
          }
        : {
            modality_label: values.modality_label?.trim(),
            modality_code: code,
            is_active: values.is_active,
          };

      const res = await makePostCall('/modality', payload);
      if (res?.data?.success) {
        message.success(`Modality ${editingRecord ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        fetchModalities();
      } else {
        throw new Error(res?.data?.message || 'Failed to save modality');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to save modality');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  const filteredModalities = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return (modalitiesList.data || []).filter(item => {
      if (statusFilter === 'active' && !item.active) return false;
      if (statusFilter === 'inactive' && item.active) return false;
      if (search) {
        const haystack = `${item.label} ${item.value}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [modalitiesList.data, statusFilter, searchText]);

  const columns = [
    {
      title: 'Modality',
      dataIndex: 'label',
      key: 'label',
      sorter: (a, b) => (a.label || '').localeCompare(b.label || ''),
    },
    {
      title: 'Code',
      dataIndex: 'value',
      key: 'value',
      sorter: (a, b) => (a.value || '').localeCompare(b.value || ''),
      render: (code) => <Tag>{code}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active, record) => (
        <Switch
          checked={active}
          loading={togglingId === record.id}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Edit Modality">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="masters-config-container">
      <div className="mc-titlebar">
        <span className="mc-title">Modality Master</span>
        <Button type="primary" className="mc-add" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Modality
        </Button>
      </div>

      <div className="mc-toolbar">
        <Space size="middle" wrap>
          <Input
            placeholder="Search by name or code"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 150 }}>
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Space>
      </div>

      {modalitiesList.error && (
        <div className="mc-error">
          <p>{modalitiesList.error}</p>
          <Button onClick={fetchModalities}>Retry</Button>
        </div>
      )}

      <div className="mc-panel">
        <div className="mc-panel-head">
          <span>Modalities</span>
          <span className="mc-count">{filteredModalities.length}</span>
        </div>
        <div className="mc-panel-body">
          <Table
            className="mc-table"
            columns={columns}
            dataSource={filteredModalities}
            loading={modalitiesList.loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} modalities`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      <Modal
        className="masters-config-modal"
        title={editingRecord ? 'Edit Modality' : 'Add Modality'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="admin-form">
          <Form.Item
            label="Modality Name"
            name="modality_label"
            rules={[
              { required: true, message: 'Please enter the modality name' },
              { max: 50, message: 'Name must be 50 characters or less' },
            ]}
          >
            <Input placeholder="e.g. Computed Tomography" />
          </Form.Item>

          <Form.Item
            label="Modality Code"
            name="modality_code"
            tooltip={editingRecord ? 'Modality code cannot be changed once created' : undefined}
            rules={[
              { required: true, message: 'Please enter the modality code' },
              { max: 60, message: 'Code must be 60 characters or less' },
            ]}
          >
            <Input
              placeholder="e.g. CT"
              disabled={!!editingRecord}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>

          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item className="form-actions">
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingRecord(null);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingRecord ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModalityMaster;
